import { type Capability, capabilitiesOf, type CoordinatorRole } from '@reditelji/shared';
import { useQuery } from '@tanstack/react-query';
import { api, ApiRequestError } from './client';

export interface Me {
  id: string;
  email: string;
  name: string;
  role: CoordinatorRole;
  /** What this login may do (SPEC §1.1L). */
  capabilities?: Capability[];
  csrfToken: string;
}

export const ME_QUERY_KEY = ['auth', 'me'] as const;

/**
 * Whether a failed request is a reason to ask /auth/me again: the session
 * ended (401), or a route refused the role (403 `capability`, SPEC §1.1L) —
 * the answer sends the layout to /login or redraws the screens for the role
 * we actually hold.
 *
 * Never for /auth/me's own failure. A query that has never succeeded goes
 * back to `pending` the moment it is fetched again, so re-asking on its own
 * 401 would loop — 401, pending, 401 — with the layout showing a spinner and
 * never the error that sends it to /login, until the rate limiter answered
 * 429 for the whole address. Its 401 is the answer; the layout reads it.
 */
export function reasksSession(error: unknown, failedKey?: readonly unknown[]): boolean {
  if (!(error instanceof ApiRequestError)) return false;
  if (failedKey && failedKey[0] === ME_QUERY_KEY[0] && failedKey[1] === ME_QUERY_KEY[1]) return false;
  return error.status === 401 || error.code === 'capability';
}

/** The signed-in coordinator. `CoordinatorLayout` renders nothing until it has loaded. */
export function useMe() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api<Me>('/auth/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The one question a screen asks before drawing a control (SPEC §1.1L): may
 * this login do it? Ask for the capability of the route the control calls.
 *
 * The capabilities come from `/auth/me`. An API that predates them — a new web
 * image served for a moment beside the old API — gets the role's own map, so
 * the marshal team never sees a read-only screen in between.
 */
export function useSession() {
  const me = useMe().data;
  const held = me ? (me.capabilities ?? capabilitiesOf(me.role)) : [];
  return {
    me,
    can: (capability: Capability) => held.includes(capability),
  };
}
